import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment-timezone';
import { useAuth } from '../../../context/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  alpha,
  useTheme,
  Autocomplete,
  TablePagination,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  LibraryBooks as LibraryBooksIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  BookmarkBorder as BookmarkBorderIcon
} from '@mui/icons-material';

import { API_BASE_URL } from '../../../config';
import { checkJournalEntriesHaveTransactions, getVoucherTemplates, VoucherTemplate } from '../../../services/financeService';
// تكوين عنوان API
const API_URL = API_BASE_URL;

// نوع البيانات لسطر القيد المحاسبي
interface JournalEntryLine {
  id: number;
  accountId: string;
  accountName: string;
  debit: number;
  credit: number;
  description: string;
}

// نوع البيانات للقيد المحاسبي
interface JournalEntry {
  id: string;
  entryNumber: string;
  date: string;
  reference: string;
  description: string;
  status: string;
  totalAmount: number;
  totalDebit?: number;
  totalCredit?: number;
  createdBy: string;
  createdAt: string;
  approvedBy?: string;
  approvedAt?: string;
  lines: JournalEntryLine[];
}

// نوع البيانات للحساب
interface Account {
  id: string;
  name: string;
  accountNumber: string;
}

interface JournalEntryTransactionStatus {
  status: 'complete' | 'partial' | 'missing';
  entryStatus: string;
  linesCount: number;
  transactionsCount: number;
  entryDebitTotal: number;
  entryCreditTotal: number;
  transactionsDebitTotal: number;
  transactionsCreditTotal: number;
  debitMatch: boolean;
  creditMatch: boolean;
  issues: string[] | null;
}

// مكون مؤشر حالة المعاملات
interface TransactionStatusIndicatorProps {
  transactionStatus: JournalEntryTransactionStatus | null;
  loading: boolean;
  entryNumber: string;
}

const TransactionStatusIndicator: React.FC<TransactionStatusIndicatorProps> = ({
  transactionStatus,
  loading,
  entryNumber
}) => {
  if (loading) {
    return <CircularProgress size={20} />;
  }

  if (!transactionStatus) {
    return <Chip label="لا توجد بيانات" size="small" color="default" />;
  }

  const getStatusIcon = (): React.ReactElement | undefined => {
    switch (transactionStatus.status) {
      case 'complete':
        return <CheckCircleIcon />;
      case 'partial':
        return <WarningIcon />;
      case 'missing':
        return <ErrorIcon />;
      default:
        return undefined;
    }
  };

  const getStatusColor = (): 'success' | 'warning' | 'error' => {
    switch (transactionStatus.status) {
      case 'complete':
        return 'success';
      case 'partial':
        return 'warning';
      case 'missing':
        return 'error';
      default:
        return 'default' as any;
    }
  };

  const getTooltipContent = () => {
    const lines = [
      `حالة القيد: ${transactionStatus.entryStatus}`,
      `عدد الأسطر: ${transactionStatus.linesCount}`,
      `عدد المعاملات: ${transactionStatus.transactionsCount}`,
      `مجموع المدين (القيد): ${transactionStatus.entryDebitTotal.toFixed(2)} ريال`,
      `مجموع المدين (المعاملات): ${transactionStatus.transactionsDebitTotal.toFixed(2)} ريال`,
      `مجموع الدائن (القيد): ${transactionStatus.entryCreditTotal.toFixed(2)} ريال`,
      `مجموع الدائن (المعاملات): ${transactionStatus.transactionsCreditTotal.toFixed(2)} ريال`,
    ];

    if (transactionStatus.issues && transactionStatus.issues.length > 0) {
      lines.push('', 'المشاكل:');
      transactionStatus.issues.forEach(issue => lines.push(`• ${issue}`));
    }

    return lines.join('\n');
  };

  return (
    <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{getTooltipContent()}</div>} arrow>
      <Chip
        icon={getStatusIcon()}
        label={
          transactionStatus.status === 'complete' ? 'مكتمل' :
          transactionStatus.status === 'partial' ? 'جزئي' : 'مفقود'
        }
        size="small"
        color={getStatusColor()}
      />
    </Tooltip>
  );
};

const emptyLine: JournalEntryLine = {
  id: 0,
  accountId: '',
  accountName: '',
  debit: 0,
  credit: 0,
  description: ''
};

function GeneralLedger() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { activeBranch } = useAuth();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<JournalEntry[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  
  // إضافة متغيرات التصفيح
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);

  // حالة المعاملات المالية
  const [entryTransactions, setEntryTransactions] = useState<Record<string, JournalEntryTransactionStatus>>({});
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  
  // حالة نموذج القيد الجديد
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentEntry, setCurrentEntry] = useState<Partial<JournalEntry>>({
    date: new Date().toISOString().split('T')[0],
    reference: '',
    description: '',
    status: 'غير مرحل',
    lines: [{ ...emptyLine, id: 1 }]
  });
  
  // حالة البحث والفلترة
  const [searchTerm, setSearchTerm] = useState('');
  const [startDateFilter, setStartDateFilter] = useState<string>('');
  const [endDateFilter, setEndDateFilter] = useState<string>('');
  const [accountFilter, setAccountFilter] = useState<string>('');
  
  // حالة تحديد قيد للحذف
  const [entryToDelete, setEntryToDelete] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  
  // حالة عرض تفاصيل القيد
  const [viewEntryId, setViewEntryId] = useState<string | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // حالة القوالب
  const [templates, setTemplates] = useState<VoucherTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<VoucherTemplate | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  
  // دالة لتحميل البيانات من API
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // جلب بيانات الحسابات النشطة للاختيار مع السياق المناسب
      const { apiWithContext } = await import('../../../services/api');
      const accountsData = await apiWithContext.get('/finance/accounts/selection/active', 'general-ledger').then(res => res.data);
      setAccounts(accountsData);
      
      // بناء URL مع pagination parameters
      let apiUrl = `${API_URL}/finance/journal-entries?page=${page + 1}&limit=${rowsPerPage}`;
      
      // إضافة البحث
      if (searchTerm) {
        apiUrl += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      // إضافة فلتر التاريخ
      if (startDateFilter) {
        apiUrl += `&startDate=${encodeURIComponent(startDateFilter)}`;
      }
      if (endDateFilter) {
        apiUrl += `&endDate=${encodeURIComponent(endDateFilter)}`;
      }
      
      console.log('Fetching journal entries with URL:', apiUrl);
      
      // جلب بيانات القيود المحاسبية
      const entriesResponse = await axios.get(apiUrl);
      
      // معالجة الاستجابة
      let entriesData: any[] = [];
      let total = 0;
      
      if (entriesResponse.data.success && entriesResponse.data.data) {
        entriesData = entriesResponse.data.data;
        total = entriesResponse.data.total || entriesResponse.data.data.length;
      } else if (Array.isArray(entriesResponse.data)) {
        // للتوافق مع الإصدارات القديمة
        entriesData = entriesResponse.data;
        total = entriesResponse.data.length;
      }
      
      // تنسيق البيانات للعرض
      const formattedEntries: JournalEntry[] = entriesData.map((entry: any) => ({
          id: entry.id,
          entryNumber: entry.entryNumber,
          date: new Date(entry.date).toISOString().split('T')[0],
          reference: entry.reference || '',
          description: entry.description,
          status: entry.status === 'مؤكد' ? 'مرحل' : 'غير مرحل',
          totalAmount: entry.totalDebit, // استخدام مجموع المدين للعرض
          totalDebit: entry.totalDebit,
          totalCredit: entry.totalCredit,
          createdBy: entry.createdBy || 'غير معروف',
          createdAt: entry.createdAt,
          approvedBy: entry.approvedBy,
          approvedAt: entry.approvedAt,
          lines: entry.lines.map((line: any, index: number) => ({
            id: index + 1,
            accountId: line.account || line.accountId,
            accountName: line.accountName,
            accountNumber: line.accountNumber,
            debit: line.debit,
            credit: line.credit,
            description: line.description || ''
          }))
        }));
        
      console.log(`Loaded ${formattedEntries.length} journal entries (total: ${total})`);
      setEntries(formattedEntries);
      setFilteredEntries(formattedEntries);
      setTotalEntries(total);
    } catch (error) {
      console.error('خطأ في تحميل البيانات:', error);
      setEntries([]);
      setFilteredEntries([]);
      setTotalEntries(0);
      // يمكن إضافة إشعار للمستخدم هنا
    } finally {
      setLoading(false);
    }
  };

  // تحميل البيانات من API
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, startDateFilter, endDateFilter]);
  
  // إعادة تعيين الصفحة عند تغيير البحث أو التواريخ
  useEffect(() => {
    setPage(0);
  }, [searchTerm, startDateFilter, endDateFilter]);
  
  // تحديث القيود المفلترة عند تغيير البحث أو الفلترة
  useEffect(() => {
    filterEntries();
  }, [searchTerm, startDateFilter, endDateFilter, accountFilter, entries]);

  // جلب بيانات المعاملات المالية للقيود
  useEffect(() => {
    const fetchEntryTransactions = async () => {
      if (entries.length === 0) return;

      // تأخير 600ms قبل جلب البيانات
      const timer = setTimeout(async () => {
        try {
          setTransactionsLoading(true);
          const entryNumbers = entries.map(e => e.entryNumber);
          const response = await checkJournalEntriesHaveTransactions(entryNumbers);
          
          if (response.success) {
            setEntryTransactions(response.results);
          }
        } catch (error) {
          console.error('Error fetching entry transactions:', error);
        } finally {
          setTransactionsLoading(false);
        }
      }, 600);

      return () => clearTimeout(timer);
    };

    fetchEntryTransactions();
  }, [entries]);
  
  // جلب القوالب المحفوظة
  useEffect(() => {
    const fetchTemplates = async () => {
      if (currentEntry.id) return; // لا نحتاج القوالب في وضع التعديل
      
      setTemplatesLoading(true);
      try {
        const templatesData = await getVoucherTemplates({ templateType: 'journal', isActive: true });
        setTemplates(templatesData);
      } catch (error) {
        console.error('خطأ في جلب القوالب:', error);
      } finally {
        setTemplatesLoading(false);
      }
    };
    
    if (isFormOpen) {
      fetchTemplates();
    }
  }, [isFormOpen, currentEntry.id]);
  
  // تطبيق القالب المختار
  const handleTemplateSelect = (template: VoucherTemplate | null) => {
    setSelectedTemplate(template);
    
    if (template) {
      const newLines: JournalEntryLine[] = [];
      let lineId = 1;
      
      // إضافة الحسابات المدينة
      if (template.debitAccounts && template.debitAccounts.length > 0) {
        template.debitAccounts.forEach(account => {
          newLines.push({
            id: lineId++,
            accountId: account.id,
            accountName: account.name,
            description: '',
            debit: 0,
            credit: 0
          });
        });
      }
      
      // إضافة الحسابات الدائنة
      if (template.creditAccounts && template.creditAccounts.length > 0) {
        template.creditAccounts.forEach(account => {
          newLines.push({
            id: lineId++,
            accountId: account.id,
            accountName: account.name,
            description: '',
            debit: 0,
            credit: 0
          });
        });
      }
      
      // تحديث النموذج بالأسطر الجديدة
      if (newLines.length > 0) {
        setCurrentEntry(prev => ({
          ...prev,
          lines: newLines
        }));
      }
    }
  };
  
  // فلترة القيود المحاسبية
  const filterEntries = () => {
    let filtered = [...entries];
    
    // فلترة بكلمة البحث
    if (searchTerm) {
      filtered = filtered.filter(entry =>
        entry.id.includes(searchTerm) ||
        entry.description.includes(searchTerm) ||
        entry.reference.includes(searchTerm)
      );
    }
    
    // فلترة بالتاريخ
    if (startDateFilter || endDateFilter) {
      filtered = filtered.filter(entry => {
        const entryDate = new Date(entry.date);
        let matchesStart = true;
        let matchesEnd = true;
        
        if (startDateFilter) {
          const startDate = new Date(startDateFilter);
          startDate.setHours(0, 0, 0, 0);
          matchesStart = entryDate >= startDate;
        }
        
        if (endDateFilter) {
          const endDate = new Date(endDateFilter);
          endDate.setHours(23, 59, 59, 999);
          matchesEnd = entryDate <= endDate;
        }
        
        return matchesStart && matchesEnd;
      });
    }
    
    // فلترة بالحساب
    if (accountFilter) {
      filtered = filtered.filter(entry => 
        entry.lines.some(line => line.accountId === accountFilter)
      );
    }
    
    setFilteredEntries(filtered);
  };
  
  // وظائف التصفيح
  const handleChangePage = (event: unknown, newPage: number) => {
    console.log(`تغيير الصفحة من ${page} إلى ${newPage}`);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    console.log(`تغيير عدد العناصر في الصفحة من ${rowsPerPage} إلى ${newRowsPerPage}`);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  // في Server-side Pagination، نستخدم entries مباشرة من API
  // لأن الخادم يرسل فقط القيود الخاصة بالصفحة الحالية
  
  // إضافة سطر جديد للقيد
  const addNewLine = () => {
    if (currentEntry.lines) {
      const newId = Math.max(...currentEntry.lines.map(line => line.id)) + 1;
      setCurrentEntry({
        ...currentEntry,
        lines: [...currentEntry.lines, { ...emptyLine, id: newId }]
      });
    }
  };
  
  // حذف سطر من القيد
  const removeLine = (lineId: number) => {
    if (currentEntry.lines && currentEntry.lines.length > 1) {
      setCurrentEntry({
        ...currentEntry,
        lines: currentEntry.lines.filter(line => line.id !== lineId)
      });
    }
  };
  
  // تحديث بيانات سطر القيد - تحسين مع استخدام نسخة عميقة من البيانات
  const updateLine = (lineId: number, field: keyof JournalEntryLine, value: any) => {
    if (currentEntry.lines) {
      // إنشاء نسخة عميقة من البيانات الحالية
      const newEntryData = JSON.parse(JSON.stringify(currentEntry));
      
      // تصحيح للتتبع
      console.log(`تحديث السطر بالمعرف ${lineId} والحقل ${String(field)} بالقيمة:`, value);
      
      const updatedLines = newEntryData.lines.map((line: JournalEntryLine) => {
        if (line.id === lineId) {
          // إنشاء نسخة جديدة من السطر
          let updatedLine = { ...line };
          
          // تحديث الحقل بطريقة آمنة للتوافق مع TypeScript
          switch (field) {
            case 'accountId':
              updatedLine.accountId = value;
              // تحديث اسم الحساب أيضًا
              const selectedAccount = accounts.find(acc => acc.id === value);
              updatedLine.accountName = selectedAccount?.name || '';
              break;
            case 'debit':
              updatedLine.debit = value;
              break;
            case 'credit':
              updatedLine.credit = value;
              break;
            case 'description':
              updatedLine.description = value;
              break;
            default:
              // إضافة تحويل النوع لتجنب خطأ TypeScript
              (updatedLine as any)[field] = value;
          }
          
          console.log(`تم تحديث السطر: ${JSON.stringify(updatedLine)}`);
          return updatedLine;
        }
        return line;
      });
      
      // تطبيق التغييرات على نسخة البيانات الجديدة
      newEntryData.lines = updatedLines;
      
      // تحديث حالة المكون
      setCurrentEntry(newEntryData);
    }
  };
  
  // احتساب المجاميع
  const calculateTotals = () => {
    if (!currentEntry.lines) return { debitTotal: 0, creditTotal: 0, isBalanced: false };
    
    const debitTotal = currentEntry.lines.reduce((sum, line) => sum + (line.debit || 0), 0);
    const creditTotal = currentEntry.lines.reduce((sum, line) => sum + (line.credit || 0), 0);
    
    return {
      debitTotal,
      creditTotal,
      isBalanced: Math.abs(debitTotal - creditTotal) < 0.01 // للتعامل مع أخطاء الفاصلة العشرية
    };
  };
  
  // حفظ القيد المحاسبي
  const saveEntry = async () => {
    const { isBalanced, debitTotal, creditTotal } = calculateTotals();
    
    if (!isBalanced) {
      alert('القيد غير متوازن. مجموع المدين يجب أن يساوي مجموع الدائن.');
      return;
    }
    
    if (!currentEntry.date) {
      alert('يرجى إدخال تاريخ القيد.');
      return;
    }

    // التحقق من وجود حسابات في السطور
    const hasEmptyAccounts = currentEntry.lines?.some(line => !line.accountId);
    if (hasEmptyAccounts) {
      alert('يرجى اختيار حساب لكل سطر في القيد.');
      return;
    }

    try {
      setLoading(true);
      
      // Get branch timezone
      const branchTimezone = activeBranch?.timezone || 'Asia/Riyadh';

      // Convert date to branch timezone with current time
      let dateToSend: string;
      if (currentEntry.date) {
        const [year, month, day] = currentEntry.date.split('-').map(Number);
        const now = moment.tz(branchTimezone);
        const dateWithTime = moment.tz({
          year,
          month: month - 1,
          day,
          hour: now.hour(),
          minute: now.minute(),
          second: now.second()
        }, branchTimezone);
        dateToSend = dateWithTime.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
      } else {
        dateToSend = moment.tz(branchTimezone).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
      }
      
      // تحويل بيانات القيد للصيغة المطلوبة في API
      const entryData = {
        date: dateToSend,
        description: currentEntry.description,
        reference: currentEntry.reference || '',
        // تحويل حالة القيد من العرض إلى القيم التي يتوقعها الـ API
        status: currentEntry.status === 'مرحل' ? 'مؤكد' : 'تحت الموافقة',
        lines: currentEntry.lines?.map(line => ({
          accountId: line.accountId, // مفتاح الحساب
          description: line.description || '',
          debit: line.debit || 0,
          credit: line.credit || 0
        })) || []
      };
      
      console.log('Saving journal entry:', entryData);
      
      let response;
      
      if (currentEntry.id) {
        // تحديث قيد موجود
        response = await axios.put(`${API_URL}/finance/journal-entries/${currentEntry.id}`, entryData);
      } else {
        // إضافة قيد جديد
        response = await axios.post(`${API_URL}/finance/journal-entries`, entryData);
      }
      
      console.log('Save response:', response.data);
      
      // تحديث قائمة القيود بعد الحفظ
      const updatedEntries = await axios.get(`${API_URL}/finance/journal-entries`);
      
      // معالجة الاستجابة بشكل صحيح
      let entriesData: any[] = [];
      if (updatedEntries.data.success && updatedEntries.data.data) {
        entriesData = updatedEntries.data.data;
      } else if (Array.isArray(updatedEntries.data)) {
        entriesData = updatedEntries.data;
      }
      
      // تنسيق البيانات للعرض
      const formattedEntries: JournalEntry[] = entriesData.map((entry: any) => ({
        id: entry.id,
        entryNumber: entry.entryNumber,
        date: new Date(entry.date).toISOString().split('T')[0],
        reference: entry.reference || '',
        description: entry.description,
        status: entry.status === 'مؤكد' ? 'مرحل' : 'غير مرحل',
        totalAmount: entry.totalDebit, // استخدام مجموع المدين للعرض
        totalDebit: entry.totalDebit,
        totalCredit: entry.totalCredit,
        createdBy: entry.createdBy || 'غير معروف',
        createdAt: entry.createdAt,
        approvedBy: entry.approvedBy,
        approvedAt: entry.approvedAt,
        lines: entry.lines.map((line: any, index: number) => ({
          id: index + 1,
          accountId: line.account || line.accountId,
          accountName: line.accountName,
          accountNumber: line.accountNumber,
          debit: line.debit,
          credit: line.credit,
          description: line.description || ''
        }))
      }));
      
      setEntries(formattedEntries);
      setFilteredEntries(formattedEntries);
      
      // عرض رسالة نجاح
      alert(currentEntry.id ? 'تم تحديث القيد بنجاح' : 'تم إنشاء القيد بنجاح');
      
      // إغلاق النموذج وإعادة تعيين الحالة
      resetForm();
    } catch (error) {
      console.error('خطأ في حفظ القيد المحاسبي:', error);
      alert('حدث خطأ أثناء حفظ القيد المحاسبي');
    } finally {
      setLoading(false);
    }
  };
  
  // إعادة تعيين حالة النموذج
  const resetForm = () => {
    setIsFormOpen(false);
    setCurrentEntry({
      date: new Date().toISOString().split('T')[0],
      reference: '',
      description: '',
      status: 'غير مرحل',
      lines: [{ ...emptyLine, id: 1 }]
    });
  };
  
  // تحميل القيد للتعديل
  const editEntry = async (entryId: string) => {
    try {
      // جلب تفاصيل القيد من API
      const response = await axios.get(`${API_URL}/finance/journal-entries/${entryId}`);
      const entryData = response.data;
      
      
      // تنسيق البيانات للتحرير
      const formattedEntry: Partial<JournalEntry> = {
        id: entryData.id,
        entryNumber: entryData.entryNumber,
        date: new Date(entryData.date).toISOString().split('T')[0],
        reference: entryData.reference || '',
        description: entryData.description,
        // تحويل حالة القيد من API إلى عرض المستخدم
        status: entryData.status === 'مؤكد' ? 'مرحل' : 'غير مرحل',
        lines: entryData.lines.map((line: any, index: number) => ({
          id: line.id || index + 1, // استخدام معرف فريد لكل سطر
          accountId: line.accountId,
          accountName: line.accountName,
          debit: line.debit,
          credit: line.credit,
          description: line.description || '',
          lineNumber: line.lineNumber || index + 1 // إضافة رقم السطر
        }))
      };
      
      setCurrentEntry(formattedEntry);
      setIsFormOpen(true);
    } catch (error) {
      console.error('Error fetching journal entry for edit:', error);
      alert('حدث خطأ أثناء تحميل بيانات القيد');
    }
  };
  
  // حذف القيد المحاسبي
  const deleteEntry = async () => {
    if (entryToDelete) {
      try {
        await axios.delete(`${API_URL}/finance/journal-entries/${entryToDelete}`);
        
        setEntries(prevEntries =>
          prevEntries.filter(entry => entry.id !== entryToDelete)
        );
        setFilteredEntries(prevFilteredEntries =>
          prevFilteredEntries.filter(entry => entry.id !== entryToDelete)
        );
        
        alert('تم حذف القيد المحاسبي بنجاح');
      } catch (error) {
        console.error('Error deleting journal entry:', error);
        alert('حدث خطأ أثناء حذف القيد المحاسبي');
      } finally {
        setEntryToDelete(null);
        setConfirmDeleteOpen(false);
      }
    }
  };
  
  // عرض تفاصيل القيد
  const viewEntry = async (entryId: string) => {
    try {
      // جلب تفاصيل القيد من API
      const response = await axios.get(`${API_URL}/finance/journal-entries/${entryId}`);
      setViewEntryId(entryId);
      setViewDialogOpen(true);
    } catch (error) {
      console.error('Error fetching journal entry details:', error);
      alert('حدث خطأ أثناء تحميل تفاصيل القيد');
    }
  };
  
  // الحصول على القيد المحدد للعرض
  const getSelectedEntry = () => {
    return entries.find(entry => entry.id === viewEntryId);
  };
  
  // عرض حالة القيد
  const renderEntryStatus = (status: string) => {
    let color: 'success' | 'warning' = 'warning';
    
    if (status === 'مرحل') {
      color = 'success';
    } else {
      color = 'warning'; // غير مرحل
    }
    
    return (
      <Chip 
        size="small" 
        label={status} 
        color={color}
        sx={{ 
          fontWeight: 500,
          fontSize: '0.75rem'
        }}
      />
    );
  };
  
  // عرض شكل نموذج القيد
  const renderEntryForm = () => {
    const { debitTotal, creditTotal, isBalanced } = calculateTotals();
    
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 2,
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid',
          borderColor: alpha('#000', 0.08),
          background: 'white'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3 
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
            <LibraryBooksIcon sx={{ mr: 1, color: '#30c3b0' }} />
            {currentEntry.id ? 'تعديل قيد محاسبي' : 'قيد محاسبي جديد'}
          </Typography>
          
          <Box>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={resetForm}
              sx={{ mr: 1, borderRadius: 2 }}
            >
              إلغاء
            </Button>
            
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={saveEntry}
              disabled={!isBalanced || !currentEntry.date}
              sx={{
                bgcolor: '#30c3b0',
                '&:hover': { bgcolor: '#25a899' },
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(48, 195, 176, 0.2)',
              }}
            >
              حفظ القيد
            </Button>
          </Box>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {/* قسم اختيار القالب - يظهر فقط في وضع الإنشاء */}
        {!currentEntry.id && (
          <Box sx={{ mb: 3, p: 2, bgcolor: alpha('#30c3b0', 0.05), borderRadius: 2, border: '1px dashed', borderColor: '#30c3b0' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', color: '#30c3b0' }}>
              <BookmarkBorderIcon sx={{ mr: 1 }} />
              استخدام قالب محفوظ (اختياري)
            </Typography>
            
            <Autocomplete
              options={templates}
              getOptionLabel={(option) => option.name}
              value={selectedTemplate}
              onChange={(_, value) => handleTemplateSelect(value)}
              loading={templatesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="اختر قالب محفوظ لملء الحسابات تلقائياً"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {templatesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.name}
                    </Typography>
                    {option.description && (
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    )}
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" display="block" color="text.secondary">
                        <strong>المدين:</strong> {option.debitAccounts?.map(acc => acc.name).join(', ')}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        <strong>الدائن:</strong> {option.creditAccounts?.map(acc => acc.name).join(', ')}
                      </Typography>
                    </Box>
                  </Box>
                </li>
              )}
            />
            
            {selectedTemplate && (
              <Alert severity="info" sx={{ mt: 2 }} icon={<BookmarkBorderIcon />}>
                تم تطبيق القالب "{selectedTemplate.name}" - تم إضافة {selectedTemplate.debitAccounts?.length || 0} حساب مدين و {selectedTemplate.creditAccounts?.length || 0} حساب دائن
              </Alert>
            )}
          </Box>
        )}
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              required
              fullWidth
              type="date"
              label="تاريخ القيد"
              value={currentEntry.date}
              onChange={(e) => setCurrentEntry({ ...currentEntry, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="رقم المرجع"
              value={currentEntry.reference}
              onChange={(e) => setCurrentEntry({ ...currentEntry, reference: e.target.value })}
              placeholder="رقم الفاتورة، أمر الشراء..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth required>
              <InputLabel id="status-label">حالة القيد</InputLabel>
              <Select
                labelId="status-label"
                id="status-select"
                value={currentEntry.status || 'غير مرحل'}
                onChange={(e) => setCurrentEntry({ ...currentEntry, status: e.target.value })}
                label="حالة القيد"
              >
                <MenuItem value="غير مرحل">غير مرحل</MenuItem>
                <MenuItem value="مرحل">مرحل</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              required
              fullWidth
              label="وصف القيد"
              value={currentEntry.description}
              onChange={(e) => setCurrentEntry({ ...currentEntry, description: e.target.value })}
              placeholder="وصف مختصر للعملية المالية"
            />
          </Grid>
        </Grid>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            تفاصيل القيد
          </Typography>
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                  <TableCell width="40%">الحساب</TableCell>
                  <TableCell>البيان</TableCell>
                  <TableCell align="right" width="15%">مدين</TableCell>
                  <TableCell align="right" width="15%">دائن</TableCell>
                  <TableCell align="center" width="80px">الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentEntry.lines && currentEntry.lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      <Autocomplete
                        size="small"
                        options={accounts}
                        value={accounts.find(acc => acc.id === line.accountId) || null}
                        onChange={(_, newValue) => 
                          updateLine(line.id, 'accountId', newValue?.id || '')
                        }
                        getOptionLabel={(option) => `${option.accountNumber} - ${option.name}`}
                        renderInput={(params) => 
                          <TextField {...params} label="اختر الحساب" variant="outlined" required />
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={line.description}
                        onChange={(e) => updateLine(line.id, 'description', e.target.value)}
                        placeholder="وصف العملية"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        fullWidth
                        value={line.debit === 0 ? '' : line.debit}
                        onChange={(e) => updateLine(line.id, 'debit', parseFloat(e.target.value) || 0)}
                        onFocus={(e) => {
                          if (line.credit > 0) {
                            updateLine(line.id, 'credit', 0);
                          }
                        }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">ر.س</InputAdornment>,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        fullWidth
                        value={line.credit === 0 ? '' : line.credit}
                        onChange={(e) => updateLine(line.id, 'credit', parseFloat(e.target.value) || 0)}
                        onFocus={(e) => {
                          if (line.debit > 0) {
                            updateLine(line.id, 'debit', 0);
                          }
                        }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">ر.س</InputAdornment>,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="حذف السطر" arrow>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeLine(line.id)}
                          disabled={currentEntry.lines?.length === 1}
                        >
                          <RemoveCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={2}>
                    <Button
                      size="small"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={addNewLine}
                      sx={{ mt: 1 }}
                    >
                      إضافة سطر
                    </Button>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {debitTotal.toLocaleString()} ر.س
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {creditTotal.toLocaleString()} ر.س
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
          
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
              p: 2,
              bgcolor: isBalanced ? alpha('#4caf50', 0.1) : alpha('#f44336', 0.1),
              borderRadius: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              color={isBalanced ? 'success.main' : 'error.main'}
              sx={{ fontWeight: 600 }}
            >
              {isBalanced ? 'القيد متوازن' : 'القيد غير متوازن'}
            </Typography>
            
            <Typography variant="subtitle1">
              الفرق: <strong>{Math.abs(debitTotal - creditTotal).toLocaleString()} ر.س</strong>
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  };
  
  // عرض جدول القيود المحاسبية
  const renderEntriesTable = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={40} sx={{ color: '#30c3b0' }} />
        </Box>
      );
    }
    
    if (filteredEntries.length === 0) {
      return (
        <Box sx={{ 
          py: 8, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          opacity: 0.8
        }}>
          <LibraryBooksIcon sx={{ fontSize: 60, color: alpha(theme.palette.primary.main, 0.3), mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد قيود محاسبية مطابقة
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            حاول تغيير معايير البحث أو إضافة قيد جديد
          </Typography>
        </Box>
      );
    }
    return (
      <>
        <TableContainer component={Box}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                <TableCell>رقم القيد</TableCell>
                <TableCell>التاريخ</TableCell>
                <TableCell>الوصف</TableCell>
                <TableCell>المرجع</TableCell>
                <TableCell align="right">المبلغ</TableCell>
                <TableCell>الحالة</TableCell>
                <TableCell>المعاملات</TableCell>
                <TableCell>المستخدم</TableCell>
                <TableCell align="center">الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map(entry => (
                <TableRow key={entry.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{entry.id}</TableCell>
                  <TableCell>{entry.date}</TableCell>
                  <TableCell>{entry.description}</TableCell>
                  <TableCell>{entry.reference}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500, color: 'primary.main' }}>
                    {entry.totalAmount.toLocaleString()} ر.س
                  </TableCell>
                  <TableCell>{renderEntryStatus(entry.status)}</TableCell>
                  <TableCell>
                    <TransactionStatusIndicator
                      transactionStatus={entryTransactions[entry.entryNumber] || null}
                      loading={transactionsLoading}
                      entryNumber={entry.entryNumber}
                    />
                  </TableCell>
                  <TableCell>{entry.createdBy}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="عرض التفاصيل" arrow>
                      <IconButton
                        color="primary"
                        onClick={() => viewEntry(entry.id)}
                        sx={{ color: '#30c3b0' }}
                      >
                        <LibraryBooksIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {/* السماح بالتعديل والحذف لجميع القيود بغض النظر عن الحالة */}
                    <Tooltip title="تعديل" arrow>
                      <IconButton
                        color="primary"
                        onClick={() => editEntry(entry.id)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="حذف" arrow>
                      <IconButton
                        color="error"
                        onClick={() => {
                          setEntryToDelete(entry.id);
                          setConfirmDeleteOpen(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {filteredEntries.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalEntries}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="عدد العناصر في الصفحة:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
            sx={{
              mt: 2,
              '& .MuiTablePagination-toolbar': {
                flexWrap: 'wrap',
                '& .MuiTablePagination-spacer': { flex: '0 1 auto' }
              }
            }}
          />
        )}
      </>
    );
  };
  
  return (
    <>
      {/* نموذج إنشاء/تعديل قيد محاسبي */}
      {isFormOpen && renderEntryForm()}
      
      {/* جدول القيود المحاسبية */}
      <Box sx={{ mb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            border: '1px solid',
            borderColor: alpha('#000', 0.08),
            background: 'white'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3 
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              <LibraryBooksIcon sx={{ mr: 1, color: '#30c3b0' }} />
              دفتر الأستاذ العام
            </Typography>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsFormOpen(true)}
              sx={{
                bgcolor: '#30c3b0',
                '&:hover': { bgcolor: '#25a899' },
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(48, 195, 176, 0.2)',
              }}
            >
              قيد جديد
            </Button>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', mb: 2, gap: 2 }}>
            <TextField
              placeholder="بحث..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: 300 }}
              size="small"
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>فلترة بالحساب</InputLabel>
                <Select
                  value={accountFilter}
                  onChange={(e) => setAccountFilter(e.target.value)}
                  label="فلترة بالحساب"
                >
                  <MenuItem value="">جميع الحسابات</MenuItem>
                  {accounts.map(account => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.accountNumber} - {account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                type="date"
                label="من تاريخ"
                value={startDateFilter}
                onChange={(e) => setStartDateFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              
              <TextField
                type="date"
                label="إلى تاريخ"
                value={endDateFilter}
                onChange={(e) => setEndDateFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              
              <Tooltip title="تحديث" arrow>
                <IconButton
                  onClick={fetchData}
                  color="primary"
                  disabled={loading}
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                >
                  {loading ? <CircularProgress size={24} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {renderEntriesTable()}
        </Paper>
      </Box>
      
      {/* مربع حوار تأكيد الحذف */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من حذف القيد المحاسبي؟
          </Typography>
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            تحذير: لا يمكن التراجع عن هذه العملية.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>إلغاء</Button>
          <Button 
            onClick={deleteEntry} 
            color="error" 
            variant="contained"
          >
            حذف
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* مربع حوار عرض تفاصيل القيد */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <LibraryBooksIcon sx={{ mr: 1, color: '#30c3b0' }} />
            تفاصيل القيد المحاسبي - {getSelectedEntry()?.id}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {getSelectedEntry() && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">رقم القيد</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{getSelectedEntry()?.id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">التاريخ</Typography>
                  <Typography variant="body1">{getSelectedEntry()?.date}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">رقم المرجع</Typography>
                  <Typography variant="body1">{getSelectedEntry()?.reference || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">الحالة</Typography>
                  <Typography variant="body1">{renderEntryStatus(getSelectedEntry()?.status || '')}</Typography>
                </Grid>
              </Grid>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">الوصف</Typography>
                <Typography variant="body1">{getSelectedEntry()?.description}</Typography>
              </Box>
              
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                تفاصيل القيد
              </Typography>
              
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), '& th': { fontWeight: 600 } }}>
                      <TableCell>الحساب</TableCell>
                      <TableCell>البيان</TableCell>
                      <TableCell align="right">مدين</TableCell>
                      <TableCell align="right">دائن</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getSelectedEntry()?.lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell>{line.accountName}</TableCell>
                        <TableCell>{line.description}</TableCell>
                        <TableCell align="right">
                          {line.debit > 0 ? `${line.debit.toLocaleString()} ر.س` : ''}
                        </TableCell>
                        <TableCell align="right">
                          {line.credit > 0 ? `${line.credit.toLocaleString()} ر.س` : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 600 }}>المجموع</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {getSelectedEntry()?.totalAmount.toLocaleString()} ر.س
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {getSelectedEntry()?.totalAmount.toLocaleString()} ر.س
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">أنشئ بواسطة</Typography>
                <Typography variant="body1">{getSelectedEntry()?.createdBy}</Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">تاريخ الإنشاء</Typography>
                <Typography variant="body1">
                  {new Date(getSelectedEntry()?.createdAt || '').toLocaleString()}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>إغلاق</Button>
          {/* السماح بالتعديل لجميع القيود بغض النظر عن الحالة */}
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              editEntry(getSelectedEntry()?.id || '');
              setViewDialogOpen(false);
            }}
          >
            تعديل
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default GeneralLedger;
